import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import mailcheck from 'mailcheck';
import { Controller } from 'react-hook-form';
import TextField from './TextField';

const EmailField = React.forwardRef(({
  value,
  onChange,
  helperText: originalHelperText,
  formatSuggestion,
  useStringOnClick,
  ...properties
}, reference) => {
  const { full: suggestedEmail } = { ...mailcheck.run({ email: value }) };
  const onClick = useCallback(
    () => {
      if (useStringOnClick) {
        onChange(suggestedEmail);
        return;
      }
      onChange({ target: { value: suggestedEmail } });
    },
    [onChange, suggestedEmail, useStringOnClick],
  );

  const emailSuggestion = (suggestedEmail && suggestedEmail !== value) && (
    <span
      onClick={onClick}
      onKeyDown={onClick}
      role="button"
      tabIndex={-1}
    >
      {formatSuggestion(suggestedEmail)}
    </span>
  );
  const helperText = emailSuggestion || originalHelperText;

  return (
    <TextField
      {...properties}
      type="text"
      onChange={onChange}
      value={value}
      helperText={helperText}
      ref={reference}
    />
  );
});

EmailField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  helperText: PropTypes.node,
  formatSuggestion: PropTypes.func,
  useStringOnClick: PropTypes.bool,
};

EmailField.defaultProps = {
  value: '',
  onChange: undefined,
  helperText: undefined,
  formatSuggestion: (email) => `Did you mean ${email}?`,
  useStringOnClick: false,
};

export const EmailFieldWithController = ({
  rules,
  name,
  control,
  defaultValue,
  ...properties
}) => {
  const render = useCallback(
    ({
      onChange, onBlur, value, ref,
    }) => (
      <EmailField
        onChange={onChange}
        onBlur={onBlur}
        value={value}
        ref={ref}
        name={name}
        {...properties}
      />
    ),
    [name, properties],
  );
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      defaultValue={defaultValue}
      render={render}
    />
  );
};

EmailFieldWithController.propTypes = {
  rules: PropTypes.shape({}),
  name: PropTypes.string.isRequired,
  control: PropTypes.shape({}).isRequired,
  defaultValue: PropTypes.string,
};

EmailFieldWithController.defaultProps = {
  defaultValue: '',
  rules: {},
};

export default EmailField;
